import { useQuery } from "@tanstack/react-query";
import { App as CapacitorApp } from "@capacitor/app";
import { Capacitor } from "@capacitor/core";
import { isVersionBelow } from "@nfp-tracker/utils";
import { supabase } from "../integrations/supabase/client";

const APP_VERSION = typeof __APP_VERSION__ === "string" ? __APP_VERSION__ : "0.0.0";

// Rarely changes — long stale time keeps this off the critical path of every app boot,
// while refetchOnWindowFocus picks up a freshly-flipped gate on the next foreground.
const RELEASE_GATE_STALE_MS = 60 * 60 * 1000;

type ReleaseGateRow = {
  platform: string;
  min_supported_version: string | null;
  update_message: string | null;
  store_url: string | null;
};

const getInstalledVersion = async (): Promise<string> => {
  if (!Capacitor.isNativePlatform()) {
    return APP_VERSION;
  }
  try {
    const info = await CapacitorApp.getInfo();
    return info.version;
  } catch {
    return APP_VERSION;
  }
};

const fetchReleaseGate = async () => {
  const platform = Capacitor.getPlatform();
  const [installedVersion, { data, error }] = await Promise.all([
    getInstalledVersion(),
    supabase.rpc("get_app_release_gate"),
  ]);

  // Fail open on any error — never block boot on a network hiccup or missing row.
  if (error) {
    return { isBlocked: false, updateMessage: null, storeUrl: null };
  }

  const row = (data as ReleaseGateRow[] | null)?.find((r) => r.platform === platform);
  if (!row) {
    return { isBlocked: false, updateMessage: null, storeUrl: null };
  }

  return {
    isBlocked: isVersionBelow(installedVersion, row.min_supported_version),
    updateMessage: row.update_message,
    storeUrl: row.store_url,
  };
};

/**
 * Fallback safety net, inactive until a real minimum version is configured server-side
 * (see app_release_gate table). Fails open on load/error so it never adds boot latency
 * or risks locking everyone out over a network hiccup.
 */
export const useMinVersionGate = () => {
  const query = useQuery({
    queryKey: ["app-release-gate"],
    queryFn: fetchReleaseGate,
    staleTime: RELEASE_GATE_STALE_MS,
    refetchOnWindowFocus: true,
  });

  return {
    isBlocked: query.data?.isBlocked ?? false,
    updateMessage: query.data?.updateMessage ?? null,
    storeUrl: query.data?.storeUrl ?? null,
  };
};
